import {configureStore, Tuple} from "@reduxjs/toolkit";
import logger from "redux-logger";
import {combineReducers} from "redux";
import {setupListeners} from "@reduxjs/toolkit/query";
import {restApi} from "./api-rest.ts";
import {coreSlice} from "./slice-core";

// only apply LogRocket's redux-logger in development mode
// eventually, move all in `.env`, but for now in React using general-config.ts
const middlewares = new Tuple();
if (import.meta.env.DEV) {
    middlewares.concat(logger);
}

// set as distinct variable for proper [RootState] type definition
const rootReducer = combineReducers({
    core: coreSlice.reducer,
    [restApi.reducerPath]: restApi.reducer
});

/**
 * NEW using Redux Toolkit (easier)
 * todo `configureStore()` automatically includes redux-thunk, so no need for `middlewares`
 *
 * @sources
 *  - https://github.com/zsajjad/rtk-demo/blob/master/app/configureStore.js
 *  - https://blog.logrocket.com/smarter-redux-with-redux-toolkit/
 *  - https://redux-toolkit.js.org/usage/usage-guide
 */
export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware()
        .concat(middlewares)
        .concat(restApi.middleware)
    ,
    preloadedState: {},
    devTools: import.meta.env.NODE_ENV !== "production",
});

/**
 * Only define app state typings in store/index.ts and custom-typed hooks are in store/hooks.ts
 * @source
 *  - https://github.com/reduxjs/react-redux/releases/tag/v9.1.0
 *  - https://redux-toolkit.js.org/tutorials/quick-start#create-a-redux-store
 *  - https://redux-toolkit.js.org/usage/usage-with-typescript#getting-the-dispatch-type
 *  - https://redux-toolkit.js.org/usage/usage-with-typescript#getting-the-state-type
 */
// infer the `RootState` types from the store itself (recommended method; else `typeof store.getState`)
export type RootState = ReturnType<typeof rootReducer>;
// infer the dispatch type from this particular store
export type XDispatch = typeof store.dispatch;

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);
